import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";

// DELETE /api/admin/content/assets/:id
//
// Folder deletes cascade via the FK on_delete=cascade — the service also
// cleans up children's storage objects best-effort before deleting the row.
// Replaces revalidatePath("/admin/content") from
// /app/admin/content/_actions.ts::deleteContentAsset.
//
// Permission gate at the worker requires "content.delete" (a separate
// permission from "content.write"); legacy parity.
export function useDeleteAsset() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, string>({
    mutationFn: (id) =>
      apiFetch<{ ok: true }>(`/admin/content/assets/${id}`, {
        method: "DELETE",
      }),
    onSuccess: (_data, id) => {
      // Folder deletes cascade in the DB; the cheapest correct invalidation
      // is to sweep the entire content namespace. The detail key for the
      // removed asset is also explicitly removed so a stale tab routed to
      // /admin/content/:id falls into the 404 branch on next visit.
      queryClient.invalidateQueries({ queryKey: queryKeys.content.all });
      queryClient.removeQueries({ queryKey: queryKeys.content.detail(id) });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to delete asset");
    },
  });
}
